import { PricingPeriod } from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import { N800, B400 } from '@atlaskit/theme/colors';
import React, { ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDesktopMediaQuery } from '../hooks';
import { usePriceFinderStore } from '../store';
import { deviceMediaQueries } from '../utils/constants';
import { PricingExampleButton } from './pricing-example/pricing-example-button';
import { Radio } from '@atlaskit/radio';

type BillingPeriodSelectorProps = {
  exampleShown: boolean;
  onShowHideExampleClick: (show: boolean) => void;
};
const BillingPeriodSelector = ({
  exampleShown,
  onShowHideExampleClick
}: BillingPeriodSelectorProps) => {
  const intl = useIntl();
  const isDesktop = useDesktopMediaQuery();
  const [{ billingCycle }, { updateBillingCycle }] = usePriceFinderStore();

  const onBillingCycleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    void updateBillingCycle(event.target.value as PricingPeriod);
  };

  return (
    <>
      <HeaderMessage>
        <FormattedMessage
          id="billingPeriodSelectorHeader"
          defaultMessage="How often would you like to be billed?"
          description="Header for billing period selector."
        />
      </HeaderMessage>

      <BillingToggle>
        <Radio
          value={'MONTHLY'}
          label={intl.formatMessage({
            id: 'monthlyBillingToggleLabel',
            defaultMessage: 'Monthly',
            description: 'Label for radio button toggle to monthly billing.'
          })}
          name="billing-toggle-radio-button"
          testId="monthly-billing-toggle-radio-button"
          isChecked={billingCycle === 'MONTHLY'}
          onChange={onBillingCycleChange}
        />
        <AnnualBillingToggle>
          <Radio
            value={'ANNUAL'}
            label={intl.formatMessage({
              id: 'annualBillingToggleLabel',
              defaultMessage: 'Annually',
              description: 'Label for radio button toggle to annual billing.'
            })}
            name="billing-toggle-radio-button"
            testId="annual-billing-toggle-radio-button"
            isChecked={billingCycle === 'ANNUAL'}
            onChange={onBillingCycleChange}
          />
        </AnnualBillingToggle>
      </BillingToggle>

      {isDesktop && (
        <ShowHideExample>
          <PricingExampleButton
            onClickHandler={() => {
              onShowHideExampleClick(!exampleShown);
            }}
          >
            {exampleShown ? (
              <FormattedMessage
                id="hideExampleLabel"
                defaultMessage="Hide pricing example"
                description="Label for hide button to close the example display."
              />
            ) : (
              <FormattedMessage
                id="showExampleLabel"
                defaultMessage="See pricing example"
                description="Label for button to show pricing example."
              />
            )}
          </PricingExampleButton>
        </ShowHideExample>
      )}
    </>
  );
};

const HeaderMessage = styled.h4`
  font-size: 16px;
  grid-column: 2;
  grid-row: 3;
  letter-spacing: 0.3px;
  line-height: 20px;
  margin: 0;
  padding-right: 30px;
  padding-top: 15px;

  ${deviceMediaQueries.desktop} {
    display: inline;
    font-size: 20px;
    line-height: 29px;
    padding-top: 2px;
  }
`;

const billingToggleBase = `
  grid-row: 3;
  display:flex;
  label {
    font-family: Charlie Text;
    font-weight: 400;
    color: ${N800};
    cursor: pointer;
    left: -5px;
    width: fit-content;
    align-items: center;
    letter-spacing:.3px;


    ${deviceMediaQueries.desktop} {  
      font-family: Charlie Display;
      font-size: 20px;
    }
  }


  
  input {
    cursor: pointer;

    + span {
      padding-left: 6px;
    }
    
    ${deviceMediaQueries.desktop} {
      + span {
        padding-left: 11px;
      }
      width: 24px;
      height: 24px;
      background-color: white;
      border-color: ${B400};
      border-width: 2px;
      transform: none;

      &:after {
        width: 14px;
        height: 14px;
        background-color: ${B400};
      }
      &:focus-visible {
        box-shadow: 0px 0px 3px 3px ${B400};  
        &:after {
          width: 14px;
          height: 14px;
          background-color: ${B400};
        }
      }
    }
  }

  ${deviceMediaQueries.tablet} {
    grid-row: 4;
  }
`;

const BillingToggle = styled.div`
  ${billingToggleBase}
  grid-column: 3;
  letter-spacing: 0.3px;

  ${deviceMediaQueries.tablet} {
    display: flex;

    label {
      margin-left: 0;
      padding: 15px;
      padding-left: 0;
    }
  }
`;

const AnnualBillingToggle = styled.div`
  ${billingToggleBase}
  grid-column: 4;
  padding-left: 30px;

  ${deviceMediaQueries.desktop} {
    padding-left: 42px;
  }
`;

const ShowHideExample = styled.div`
  grid-column: 5;
  grid-row: 3;
  button {
    line-height: 14px;
    margin-left: 30px;

    ${deviceMediaQueries.desktop} {
      line-height: 32px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

export { BillingPeriodSelector };
